import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import PageShell, { PageHero, Plate, Steps } from "./PageShell";
import { Arrow, Display, Eyebrow } from "../components/Primitives";
import { pickLang } from "../copy";
import { route } from "../paths";

/** Photo-estimate explainer: send photos → get a quote → accept or decline. */
const DevisPageV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const isFr = lang === "fr";

  const steps = isFr
    ? [
        {
          title: "Envoyez des photos",
          desc: "Décrivez votre besoin (tache tenace, petite retouche, matière spéciale…) et ajoutez quelques photos depuis votre espace client.",
          meta: "2 minutes",
        },
        {
          title: "Recevez votre devis",
          desc: "Notre équipe étudie votre demande et vous propose un prix sur mesure, notifié par email.",
          meta: "Sous 24 – 48h",
        },
        {
          title: "Validez ou refusez",
          desc: "Vous acceptez ? Paiement sécurisé en ligne et la prestation démarre. Sinon, refusez sans frais.",
          meta: "Sans engagement",
        },
      ]
    : [
        {
          title: "Send photos",
          desc: "Describe your need (stubborn stain, small touch-up, special material…) and add a few photos from your account.",
          meta: "2 minutes",
        },
        {
          title: "Receive your quote",
          desc: "Our team reviews your request and offers a tailored price, notified by email.",
          meta: "Within 24 – 48h",
        },
        {
          title: "Accept or decline",
          desc: "You accept? Secure online payment and the service starts. Otherwise, decline at no cost.",
          meta: "No commitment",
        },
      ];

  const cases = isFr
    ? [
        "Tache impossible",
        "Cuir exotique",
        "Jaunissement",
        "Couture à reprendre",
        "Œillet abîmé",
        "Paire vintage",
      ]
    : [
        "Impossible stain",
        "Exotic leather",
        "Yellowing",
        "Loose stitching",
        "Damaged eyelet",
        "Vintage pair",
      ];

  return (
    <PageShell
      seo={{
        title: isFr
          ? "Devis gratuit sur photos : nettoyage et retouches de baskets | Lave Tes Baskets"
          : "Free photo estimate: sneaker cleaning and touch-ups | Lave Tes Baskets",
        description: isFr
          ? "Tache tenace, couture à reprendre, cuir fragile ? Envoyez des photos de vos baskets, recevez un devis gratuit sous 24-48h et validez en ligne. Sans engagement, Île-de-France."
          : "Stubborn stain, loose stitching, fragile leather? Send photos of your sneakers, get a free estimate within 24-48h and accept online. No commitment, Île-de-France.",
        canonicalPath: route(lang, "devis"),
        jsonLd: [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: isFr
              ? "Devis personnalisé nettoyage et petites retouches"
              : "Custom cleaning and touch-up estimate",
            provider: { "@type": "LocalBusiness", name: "Lave Tes Baskets" },
            areaServed: "Île-de-France",
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
          },
        ],
      }}
    >
      <PageHero
        crumbs={[
          { label: isFr ? "Accueil" : "Home", to: route(lang) },
          { label: isFr ? "Devis" : "Estimate" },
        ]}
        index="04"
        eyebrow={isFr ? "Devis photo · Gratuit" : "Photo estimate · Free"}
        title={
          isFr ? (
            <>
              Montrez-nous,{" "}
              <em className="italic text-tangerine">on chiffre.</em>
            </>
          ) : (
            <>
              Show us, <em className="italic text-tangerine">we quote.</em>
            </>
          )
        }
        lede={
          isFr
            ? "Tache impossible, couture à reprendre, matière fragile ? Quelques photos et on vous répond avec un prix sur mesure, sans engagement."
            : "Impossible stain, loose stitching, fragile material? A few photos and we reply with a tailored price, no commitment."
        }
        actions={
          <>
            <Link to={`/${lang}/dashboard`} className="v2-btn v2-btn-solid">
              {isFr ? "Demander mon devis" : "Request my estimate"}
              <Arrow />
            </Link>
            <Link
              to={route(lang, "sur-mesure")}
              className="v2-btn v2-btn-ghost"
            >
              {isFr ? "Ou composer un service" : "Or build a service"}
            </Link>
          </>
        }
        aside={
          <img
            src="/illustrations/landing/nettoyage.webp"
            alt=""
            className="ml-auto w-full max-w-[440px] object-contain"
          />
        }
      />

      <section className="bg-bone py-20 text-ink sm:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Eyebrow index="01">
            {isFr ? "Comment ça marche" : "How it works"}
          </Eyebrow>
          <Display className="mt-5 max-w-3xl text-[clamp(2.2rem,5.5vw,5rem)]">
            {isFr
              ? "Trois étapes, zéro surprise."
              : "Three steps, zero surprises."}
          </Display>
          <div className="mt-12">
            <Steps steps={steps} />
          </div>

          <div
            className="v2-reveal mt-14 flex flex-wrap items-center gap-2"
            data-delay="2"
          >
            <span className="mr-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">
              {isFr ? "Cas typiques" : "Typical cases"}
            </span>
            {cases.map((c) => (
              <span
                key={c}
                className="rounded-full border border-ink/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em]"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-16 text-bone sm:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Plate
            tone="tangerine"
            title={
              isFr
                ? "Prêt à redonner vie à vos paires ?"
                : "Ready to bring your pairs back to life?"
            }
            sub={
              isFr
                ? "Créez votre compte, envoyez vos photos. Réponse sous 24 à 48h."
                : "Create your account, send your photos. Reply within 24-48h."
            }
            img="/illustrations/landing/livraison.webp"
            cta={
              <Link to={`/${lang}/dashboard`} className="v2-btn v2-btn-ink">
                {isFr ? "Demander mon devis" : "Request my estimate"}
                <Arrow />
              </Link>
            }
          />
        </div>
      </section>
    </PageShell>
  );
};

export default DevisPageV2;
